/* eslint-disable import/no-duplicates */
import React, { useState } from 'react';

import { MdArrowBack, MdArrowForward } from 'react-icons/md';

import ptBR from 'date-fns/locale/pt-BR';

import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
} from 'date-fns';

import {
  CalendarWrapper,
  Day,
  DaysOfWeek,
  Header,
  DaysContainer,
  ButtonNexPrevMonth,
  MonthName,
  ButtonNextPrevWrapper,
} from './styles';

type CustomCalendarProps = {
  startDate: Date | null;
  setStartDate: (date: Date | null) => void;
  endDate: Date | null;
  setEndDate: (date: Date | null) => void;
  customizing: boolean;
};

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  customizing,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const startOfCurrentMonth = startOfMonth(currentDate);
  const endOfCurrentMonth = endOfMonth(currentDate);
  const startOfCurrentWeek = startOfWeek(startOfCurrentMonth, {
    weekStartsOn: 1,
  });
  const endOfCurrentWeek = endOfWeek(endOfCurrentMonth, { weekStartsOn: 1 });

  const daysInMonth = eachDayOfInterval({
    start: startOfCurrentWeek,
    end: endOfCurrentWeek,
  });

  const handleDayClick = (date: Date): void => {
    if (!customizing) return;
    if (!startDate) {
      setStartDate(date);
    } else if (!endDate) {
      if (date >= startDate) {
        setEndDate(date);
      } else {
        setStartDate(date);
        setEndDate(null);
      }
    } else {
      setStartDate(date);
      setEndDate(null);
    }
  };

  const isInRange = (date: Date): boolean => {
    if (!startDate || !endDate) return false;
    return date > startDate && date < endDate;
  };

  const isStartDate = (date: Date): boolean | null => {
    return startDate && date.getTime() === startDate.getTime();
  };

  const isEndDate = (date: Date): boolean | null => {
    return endDate && date.getTime() === endDate.getTime();
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  const handleClickNextMonth = (): void => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  const handleClickPreviousMonth = (): void => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  return (
    <CalendarWrapper>
      <Header>
        <MonthName>
          {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
        </MonthName>
        <ButtonNextPrevWrapper>
          <ButtonNexPrevMonth onClick={handleClickPreviousMonth}>
            <MdArrowBack color="#3D3D3D" size={14} />
          </ButtonNexPrevMonth>
          <ButtonNexPrevMonth onClick={handleClickNextMonth}>
            <MdArrowForward color="#3D3D3D" size={14} />
          </ButtonNexPrevMonth>
        </ButtonNextPrevWrapper>
      </Header>
      <DaysOfWeek>
        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'].map((day) => (
          <div key={day}>{day}</div>
        ))}
      </DaysOfWeek>
      <DaysContainer>
        {daysInMonth.map((date) => (
          <Day
            key={date.toString()}
            isToday={isToday(date)}
            isStartDate={!!isStartDate(date)}
            isEndDate={!!isEndDate(date)}
            isInRange={!!isInRange(date)}
            isCurrentMonth={isCurrentMonth(date)}
            onClick={() => handleDayClick(date)}
          >
            {format(date, 'd')}
          </Day>
        ))}
      </DaysContainer>
    </CalendarWrapper>
  );
};

export default CustomCalendar;
